/**
 * App-Routing (LEH-002 + LEH-024; Authentik/OIDC seit issue #6).
 *
 * - Dashboard und FAUF-Routen sind mit <ProtectedRoute> gesichert wenn OIDC_CONFIGURED.
 * - Ohne OIDC_CONFIGURED (lokaler Scaffold ohne IdP) bleiben
 *   die Seiten offen, damit der Stack weiter lauffähig ist.
 * - Auth-Routen (/login, /callback) werden nur gerendert wenn OIDC aktiv ist.
 */

import { Routes, Route, Navigate } from "react-router-dom"
import { lazy, Suspense } from "react"

import Login from "@/pages/auth/Login"
import Callback from "@/pages/auth/Callback"
import { ProtectedRoute } from "@/components/auth/ProtectedRoute"
import { OIDC_CONFIGURED } from "@/lib/runtime-env"

// Lazy-load heavier pages
const Dashboard = lazy(() => import("@/pages/dashboard/Dashboard"))
const FaufList = lazy(() => import("@/pages/fauf/FaufList"))
const FaufDetail = lazy(() => import("@/pages/fauf/FaufDetail"))
// Terminal — no auth, outside AppShell/ProtectedRoute (LEH-051)
const TerminalPage = lazy(() => import("@/pages/terminal/TerminalPage"))

// Stammdaten Fertigung (LEH-090)
const KostenstellenPage = lazy(() => import("@/pages/stammdaten/KostenstellenPage"))
const MaschinenPage = lazy(() => import("@/pages/stammdaten/MaschinenPage"))
const ArbeitsgangtypenPage = lazy(() => import("@/pages/stammdaten/ArbeitsgangtypenPage"))
const KostentraegerPage = lazy(() => import("@/pages/stammdaten/KostentraegerPage"))

// Stammdaten Artikel + CRM (LEH-091 / LEH-099)
const ArtikelPage = lazy(() => import("@/pages/stammdaten/ArtikelPage"))
const OrganisationenPage = lazy(() => import("@/pages/stammdaten/OrganisationenPage"))
const AdresstypenPage = lazy(() => import("@/pages/stammdaten/AdresstypenPage"))
const KontaktrollenPage = lazy(() => import("@/pages/stammdaten/KontaktrollenPage"))

// Einkauf / Beschaffung (LEH-093)
const BestellungenPage = lazy(() => import("@/pages/einkauf/BestellungenPage"))
const BestellungDetail = lazy(() => import("@/pages/einkauf/BestellungDetail"))
const LagerPage = lazy(() => import("@/pages/einkauf/LagerPage"))
const EinkaufspreisePage = lazy(() => import("@/pages/einkauf/EinkaufspreisePage"))
const AnfragenPage = lazy(() => import("@/pages/einkauf/AnfragenPage"))
const EingangsrechnungenPage = lazy(() => import("@/pages/einkauf/EingangsrechnungenPage"))
const EingangsrechnungDetail = lazy(() => import("@/pages/einkauf/EingangsrechnungDetail"))

// Erw. Fertigung (LEH-094)
const WerkzeugePage = lazy(() => import("@/pages/stammdaten/WerkzeugePage"))
const NcProgrammePage = lazy(() => import("@/pages/stammdaten/NcProgrammePage"))
const PruefmittelPage = lazy(() => import("@/pages/stammdaten/PruefmittelPage"))
const MaengelkatalogPage = lazy(() => import("@/pages/stammdaten/MaengelkatalogPage"))

// Wartung (LEH-094)
const WartungPage = lazy(() => import("@/pages/wartung/WartungPage"))

// Einstellungen: Terminals (LEH-094)
const TerminalsPage = lazy(() => import("@/pages/einstellungen/TerminalsPage"))

// Dokumente (LEH-094)
const DokumentePage = lazy(() => import("@/pages/dokumente/DokumentePage"))

// Personal / HR (LEH-092)
const MitarbeiterPage = lazy(() => import("@/pages/hr/MitarbeiterPage"))
const MitarbeiterDetail = lazy(() => import("@/pages/hr/MitarbeiterDetail"))
const ArbeitszeitmodellePage = lazy(() => import("@/pages/hr/ArbeitszeitmodellePage"))
const UrlaubPage = lazy(() => import("@/pages/hr/UrlaubPage"))
const BetriebskalenderPage = lazy(() => import("@/pages/hr/BetriebskalenderPage"))

function PageLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">
      Laden…
    </div>
  )
}

function withProtection(element: React.ReactNode): React.ReactNode {
  return OIDC_CONFIGURED ? <ProtectedRoute>{element}</ProtectedRoute> : element
}

export default function App() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Dashboard — canonical path */}
        <Route
          path="/dashboard"
          element={withProtection(<Dashboard />)}
        />
        {/* Root redirects to dashboard */}
        <Route
          path="/"
          element={<Navigate to="/dashboard" replace />}
        />

        {/* FAUF routes */}
        <Route
          path="/fauf"
          element={withProtection(<FaufList />)}
        />
        <Route
          path="/fauf/:id"
          element={withProtection(<FaufDetail />)}
        />

        {/* Stammdaten Fertigung (LEH-090) */}
        <Route
          path="/stammdaten/kostenstellen"
          element={withProtection(<KostenstellenPage />)}
        />
        <Route
          path="/stammdaten/maschinen"
          element={withProtection(<MaschinenPage />)}
        />
        <Route
          path="/stammdaten/arbeitsgangtypen"
          element={withProtection(<ArbeitsgangtypenPage />)}
        />
        <Route
          path="/stammdaten/kostentraeger"
          element={withProtection(<KostentraegerPage />)}
        />

        {/* Erw. Fertigung (LEH-094) */}
        <Route
          path="/stammdaten/werkzeuge"
          element={withProtection(<WerkzeugePage />)}
        />
        <Route
          path="/stammdaten/nc-programme"
          element={withProtection(<NcProgrammePage />)}
        />
        <Route
          path="/stammdaten/pruefmittel"
          element={withProtection(<PruefmittelPage />)}
        />
        <Route
          path="/stammdaten/maengelkatalog"
          element={withProtection(<MaengelkatalogPage />)}
        />

        {/* Wartung (LEH-094) */}
        <Route
          path="/wartung"
          element={withProtection(<WartungPage />)}
        />

        {/* Einstellungen: Terminals (LEH-094) */}
        <Route
          path="/einstellungen/terminals"
          element={withProtection(<TerminalsPage />)}
        />

        {/* Dokumente (LEH-094) */}
        <Route
          path="/dokumente"
          element={withProtection(<DokumentePage />)}
        />

        {/* Stammdaten Artikel + CRM (LEH-091) */}
        <Route
          path="/stammdaten/artikel"
          element={withProtection(<ArtikelPage />)}
        />
        <Route
          path="/stammdaten/organisationen"
          element={withProtection(<OrganisationenPage />)}
        />
        <Route
          path="/stammdaten/adresstypen"
          element={withProtection(<AdresstypenPage />)}
        />
        <Route
          path="/stammdaten/kontaktrollen"
          element={withProtection(<KontaktrollenPage />)}
        />

        {/* Einkauf / Beschaffung (LEH-093) */}
        <Route
          path="/einkauf/bestellungen"
          element={withProtection(<BestellungenPage />)}
        />
        <Route
          path="/einkauf/bestellungen/:id"
          element={withProtection(<BestellungDetail />)}
        />
        <Route
          path="/einkauf/lager"
          element={withProtection(<LagerPage />)}
        />
        <Route
          path="/einkauf/einkaufspreise"
          element={withProtection(<EinkaufspreisePage />)}
        />
        <Route
          path="/einkauf/anfragen"
          element={withProtection(<AnfragenPage />)}
        />
        <Route
          path="/einkauf/eingangsrechnungen"
          element={withProtection(<EingangsrechnungenPage />)}
        />
        {/* Muster B: Die Eingangsrechnung ist ein Beleg, an dem gearbeitet
            wird (Positionen prüfen, Match auslösen, freigeben) — deshalb eine
            eigene, verlinkbare Seite statt eines Dialogs. */}
        <Route
          path="/einkauf/eingangsrechnungen/:id"
          element={withProtection(<EingangsrechnungDetail />)}
        />

        {/* Personal / HR (LEH-092) */}
        <Route
          path="/personal/mitarbeiter"
          element={withProtection(<MitarbeiterPage />)}
        />
        <Route
          path="/personal/mitarbeiter/:id"
          element={withProtection(<MitarbeiterDetail />)}
        />
        <Route
          path="/personal/arbeitszeitmodelle"
          element={withProtection(<ArbeitszeitmodellePage />)}
        />
        <Route
          path="/personal/urlaub"
          element={withProtection(<UrlaubPage />)}
        />
        <Route
          path="/personal/betriebskalender"
          element={withProtection(<BetriebskalenderPage />)}
        />

        {/* Auth-Routen nur wenn OIDC konfiguriert. */}
        {OIDC_CONFIGURED && <Route path="/login" element={<Login />} />}
        {OIDC_CONFIGURED && <Route path="/callback" element={<Callback />} />}

        {/* BDE Terminal — kein OIDC, kein ProtectedRoute (LEH-051) */}
        <Route path="/terminal" element={<TerminalPage />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  )
}
